import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createCycle,
  deleteCycle,
  getCycleKpis,
  getCycles,
  saveCycleSessions,
  updateCycle,
  CycleDeleteImpact,
  CycleFilters,
  CyclePayload,
  CycleSessionInput,
} from '@/api/cyclesService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

const PAGE_SIZE = 30;

const errorDescription = (error: unknown): string => {
  const res = (error as { response?: { data?: { message?: string; impact?: CycleDeleteImpact } } })?.response;
  if (res?.data?.impact && res.data.impact.plans.length > 0) {
    const names = res.data.impact.plans.map((p) => p.name).join(', ');
    return `Utilisé par : ${names}.`;
  }
  if (res?.data?.message) return res.data.message;
  return error instanceof Error ? error.message : 'Une erreur est survenue';
};

export const useCycles = (filters: { search?: string; filter?: string; page: number }) => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pv2', 'cycles'] });

  const listFilters: CycleFilters = {
    search: filters.search,
    filter: filters.filter,
    page: filters.page,
    pageSize: PAGE_SIZE,
  };

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.pv2Cycles(filters),
    queryFn: () => getCycles(listFilters),
  });

  const { data: kpis } = useQuery({
    queryKey: queryKeys.pv2CycleKpis,
    queryFn: getCycleKpis,
  });

  const onWriteSuccess = (title: string) => {
    invalidate();
    queryClient.invalidateQueries({ queryKey: queryKeys.pv2CycleKpis });
    toaster.create({ title, type: 'success' });
  };

  const createMutation = useMutation({
    mutationFn: (payload: CyclePayload) => createCycle(payload),
    onSuccess: () => onWriteSuccess('Cycle créé'),
  });

  const duplicateMutation = useMutation({
    mutationFn: async ({ payload, sessions }: { payload: CyclePayload; sessions: CycleSessionInput[] }) => {
      const created = await createCycle(payload);
      if (sessions.length > 0) {
        try {
          await saveCycleSessions(created.id, sessions);
        } catch (error) {
          // The cycle exists either way; the admin fixes the grid on the Séances tab.
          toaster.create({
            title: 'Cycle créé, séances non copiées',
            description: errorDescription(error),
            type: 'warning',
          });
        }
      }
      return created;
    },
    onSuccess: () => onWriteSuccess('Cycle dupliqué'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CyclePayload }) => updateCycle(id, payload),
    onSuccess: (cycle) => {
      onWriteSuccess('Cycle mis à jour');
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2Cycle(cycle.id) });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCycle(id),
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: queryKeys.pv2CycleKpis });
      toaster.create({ title: 'Cycle supprimé', type: 'success' });
    },
    onError: (error) =>
      toaster.create({ title: 'Suppression impossible', description: errorDescription(error), type: 'error' }),
  });

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize: PAGE_SIZE,
    kpis,
    isLoading,
    createCycle: createMutation.mutateAsync,
    createCycleWithSessions: duplicateMutation.mutateAsync,
    updateCycle: updateMutation.mutateAsync,
    isSaving: createMutation.isPending || duplicateMutation.isPending || updateMutation.isPending,
    deleteCycle: deleteMutation.mutateAsync,
    invalidate,
  };
};
